import "../../components/EventUserDashboard.css";
import AdminBackBar from "../../components/admin/AdminBackBar";
import Billede1 from "../../Images/Billede1.jpg";
import HeroSectionV2 from "../../components/HeroSectionV2";
import React, { useEffect, useMemo, useState } from "react";
import {
    Select,
    Paper,
    Title,
    Text,
    Stack,
    Grid,
    Group,
    Button,
    Alert,
    Badge,
    SimpleGrid,
} from "@mantine/core";
import QRCode from "react-qr-code";

interface EventDto {
    eventID: number;
    eventName?: string;
}

interface DrinkTicketCollectionDto {
    drinkTicketCollectionId: number;
    collectionName: string;
    eventBarId: number;
    maxDrinksPerTicket: number;
    ticketCount: number;
    createdAt?: string;
    validDate?: string | null;
}

interface DrinkTicketDto {
    drinkTicketId: number;
    ticketCode: string;
    collectionName: string;
    ticketNumber: number;
    eventBarId: number;
    maxDrinks: number;
    isActive: boolean;
}

type EventBar = {
    eventBarId: number;
    name: string;
    isActive: boolean;
};

const toArray = <T,>(result: any): T[] => {
    if (Array.isArray(result)) return result;
    if (Array.isArray(result?.data)) return result.data;
    if (Array.isArray(result?.items)) return result.items;
    if (Array.isArray(result?.results)) return result.results;
    return [];
};

const formatDate = (value?: string | null) => {
    if (!value) return "";

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
        return "";
    }

    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();

    return `${day}-${month}-${year}`;
};

const AdminDrinkTicketPrint: React.FC = () => {
    const apiUrl = process.env.REACT_APP_API_URL;

    const [events, setEvents] = useState<EventDto[]>([]);
    const [eventId, setEventId] = useState<number>(0);
    const [bars, setBars] = useState<EventBar[]>([]);

    const [collections, setCollections] = useState<DrinkTicketCollectionDto[]>([]);
    const [selectedCollectionId, setSelectedCollectionId] = useState<number>(0);
    const [tickets, setTickets] = useState<DrinkTicketDto[]>([]);

    const [error, setError] = useState<string | null>(null);
    const [loadingCollections, setLoadingCollections] = useState(false);
    const [loadingTickets, setLoadingTickets] = useState(false);

    useEffect(() => {
        const loadEvents = async () => {
            try {
                const res = await fetch(`${apiUrl}/api/events`);
                if (!res.ok) return;

                const result = await res.json();
                setEvents(toArray<EventDto>(result));
            } catch (err) {
                console.error("Fejl ved hentning af events:", err);
            }
        };

        loadEvents();
    }, [apiUrl]);

    const loadBars = async (id: number) => {
        try {
            const res = await fetch(`${apiUrl}/api/EventBars/event/${id}`);

            if (!res.ok) return;

            const result = await res.json();
            setBars(toArray<EventBar>(result));
        } catch (err) {
            console.error(err);
            setBars([]);
        }
    };

    const getBarName = (eventBarId: number) => {
        return (
            bars.find(b => b.eventBarId === eventBarId)?.name ??
            `Bar ${eventBarId}`
        );
    };

    const loadCollections = async (currentEventId: number) => {
        if (!currentEventId) {
            setCollections([]);
            return;
        }

        try {
            setLoadingCollections(true);
            setError(null);

            const res = await fetch(`${apiUrl}/api/admin/drink-ticket-collections?eventId=${currentEventId}`);
            if (!res.ok) {
                const text = await res.text();
                throw new Error(text || "Kunne ikke hente collections");
            }

            const result = await res.json();
            setCollections(toArray<DrinkTicketCollectionDto>(result));
        } catch (err: any) {
            console.error("Fejl ved hentning af collections:", err);
            setCollections([]);
            setError(err?.message || "Kunde ikke hente collections");
        } finally {
            setLoadingCollections(false);
        }
    };

    const loadTickets = async (collectionId: number) => {
        if (!collectionId) {
            setTickets([]);
            return;
        }

        try {
            setLoadingTickets(true);
            setError(null);

            const res = await fetch(`${apiUrl}/api/admin/drink-ticket-collections/${collectionId}/tickets`);
            if (!res.ok) {
                const text = await res.text();
                throw new Error(text || "Kunne ikke hente billetter");
            }

            const result = await res.json();
            setTickets(toArray<DrinkTicketDto>(result));
        } catch (err: any) {
            console.error("Fejl ved hentning af billetter:", err);
            setTickets([]);
            setError(err?.message || "Kunde ikke hente billetter");
        } finally {
            setLoadingTickets(false);
        }
    };

    useEffect(() => {
        setSelectedCollectionId(0);
        setTickets([]);
        setError(null);

        if (eventId > 0) {
            loadBars(eventId);
            loadCollections(eventId);
        } else {
            setBars([]);
            setCollections([]);
        }
    }, [eventId]);

    useEffect(() => {
        if (selectedCollectionId > 0) {
            loadTickets(selectedCollectionId);
        } else {
            setTickets([]);
        }
    }, [selectedCollectionId]);

    const selectedCollection = useMemo(() => {
        return collections.find((x) => x.drinkTicketCollectionId === selectedCollectionId) ?? null;
    }, [collections, selectedCollectionId]);

    const printPage = () => {
        window.print();
    };

    const getQrValue = (ticket: DrinkTicketDto) => {
        return `${window.location.origin}/form-drink-order/${eventId}/${encodeURIComponent(ticket.ticketCode)}?eventBarId=${selectedCollection?.eventBarId}`;
    };

    return (
        <div>

            <HeroSectionV2
                titleEn="Print Ticket Codes"
                titleDa="Print Ticket Koderne"
                descriptionEn="Select event and collection to print QR codes"
                descriptionDa="Vælg et event og collection for at printe QR koderne"
                imageSrc={Billede1}
            />

            <AdminBackBar
                title="Drinks"
                crumbs={[
                    { label: "Admin", to: "/admin" },
                    { label: "Drinks" }
                ]}
            />


            <Stack p="xl" gap="xl">

                {error && (

                    <Alert
                        color="red"
                        className="no-print"
                    >
                        {error}
                    </Alert>

                )}

                <Paper
                    shadow="sm"
                    radius="md"
                    p="xl"
                    withBorder
                    className="no-print"
                >

                    <Stack>


                        <Title order={4}>
                            Selection
                        </Title>



                        <Grid>


                            <Grid.Col
                                span={{
                                    base: 12,
                                    md: 4
                                }}
                            >

                                <Select
                                    label="Event"
                                    placeholder="Select event"
                                    value={
                                        eventId
                                            ? String(eventId)
                                            : null
                                    }
                                    onChange={(val) =>
                                        setEventId(
                                            val
                                                ? Number(val)
                                                : 0
                                        )
                                    }
                                    data={
                                        events.map(e => ({
                                            value: String(e.eventID),
                                            label:
                                                e.eventName ||
                                                `Event ${e.eventID}`
                                        }))
                                    }
                                    searchable
                                />


                            </Grid.Col>






                            <Grid.Col
                                span={{
                                    base: 12,
                                    md: 4
                                }}
                            >


                                <Select
                                    label="Collection"
                                    searchable
                                    clearable
                                    placeholder={
                                        loadingCollections
                                            ? "Loading collections..."
                                            : "Select collection"
                                    }
                                    value={
                                        selectedCollectionId
                                            ? String(selectedCollectionId)
                                            : null
                                    }
                                    onChange={(val) =>
                                        setSelectedCollectionId(
                                            val
                                                ? Number(val)
                                                : 0
                                        )
                                    }
                                    data={
                                        collections.map(c => ({
                                            value:
                                                String(
                                                    c.drinkTicketCollectionId
                                                ),

                                            label:
                                                `${c.collectionName} · ${getBarName(c.eventBarId)} · ${c.ticketCount} pcs`
                                                +
                                                (
                                                    c.validDate
                                                        ? ` · ${formatDate(c.validDate)}`
                                                        : ""
                                                )
                                        }))
                                    }
                                />


                            </Grid.Col>






                            <Grid.Col
                                span={{
                                    base: 12,
                                    md: 4
                                }}
                            >

                                <Button
                                    fullWidth
                                    mt="md"
                                    onClick={printPage}
                                    disabled={
                                        !selectedCollection ||
                                        tickets.length === 0
                                    }
                                >
                                    Print
                                </Button>


                            </Grid.Col>



                        </Grid>



                    </Stack>


                </Paper>









                {
                    selectedCollection &&

                    <div
                        className="print-only-area"
                    >


                        {
                            loadingTickets ?

                                <Text className="no-print">
                                    Loading tickets...
                                </Text>


                                :


                                <SimpleGrid
                                    cols={{
                                        base: 1,
                                        sm: 2,
                                        md: 4
                                    }}
                                    spacing="md"
                                    className="tickets-print-grid"
                                >


                                    {
                                        tickets.map(ticket => (


                                            <Paper
                                                key={
                                                    ticket.drinkTicketId
                                                }
                                                className="ticket-print-card"
                                                shadow="sm"
                                                radius="md"
                                                p="md"
                                                withBorder
                                            >


                                                <Stack
                                                    align="center"
                                                    gap="xs"
                                                >


                                                    <QRCode
                                                        value={
                                                            getQrValue(ticket)
                                                        }
                                                        size={190}
                                                        bgColor="#FFFFFF"
                                                        fgColor="#000000"
                                                        level="M"
                                                    />



                                                    <Text
                                                        fw={800}
                                                    >
                                                        {
                                                            ticket.ticketCode
                                                                ?.replace(
                                                                    /-\d+-/,
                                                                    "-"
                                                                )
                                                        }
                                                    </Text>





                                                    {
                                                        selectedCollection.validDate &&

                                                        <Text size="sm">
                                                            Only valid{" "}
                                                            {
                                                                formatDate(
                                                                    selectedCollection.validDate
                                                                )
                                                            }
                                                        </Text>

                                                    }





                                                    <Badge>
                                                        {
                                                            getBarName(
                                                                selectedCollection.eventBarId
                                                            )
                                                        }
                                                    </Badge>




                                                    <Text size="sm">

                                                        Drinks:{" "}
                                                        {
                                                            ticket.maxDrinks
                                                        }

                                                    </Text>



                                                </Stack>



                                            </Paper>



                                        ))
                                    }



                                </SimpleGrid>


                        }



                    </div>

                }



            </Stack>





            <style>{`
            @media print {

                @page {
                    size:A4;
                    margin:4mm;
                }


                body * {
                    visibility:hidden;
                }


                .print-only-area,
                .print-only-area * {
                    visibility:visible;
                }


                .print-only-area {
                    position:absolute;
                    top:0;
                    left:0;
                    width:100%;
                }


                .no-print {
                    display:none !important;
                }


                .tickets-print-grid {
                    display:grid !important;
                    grid-template-columns:repeat(4,1fr) !important;
                    gap:0 !important;
                }


                .ticket-print-card {

                    height:71mm !important;
                    min-height:71mm !important;

                    border:1px solid black !important;
                    border-radius:0 !important;

                    box-shadow:none !important;

                    padding:2mm !important;

                    page-break-inside:avoid;
                    break-inside:avoid;

                }


                .ticket-print-card svg {

                    width:120px !important;
                    height:120px !important;

                }


            }

        `}</style>


        </div>
    );
};

export default AdminDrinkTicketPrint;